import { useContext, useEffect, useMemo, useState } from "react";
import UIkit from "uikit";

import { AppContexts } from "../../../providers";
import TagGroupsService from "../../../services/tag-groups.service";
import FeatherIcon from "../../common/FeatherIcon";

const byName = (a, b) => a.name.localeCompare(b.name);

const TagGroups = () => {
  const { active, settings, setSettings } = useContext(
    AppContexts.SettingsContext,
  );
  const [selectedId, setSelectedId] = useState(null);
  const [label, setLabel] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const groups = useMemo(
    () => settings.tag_groups || [],
    [settings.tag_groups],
  );
  const tags = settings.tags || [];
  const selected = groups.find((group) => group.id === selectedId) || groups[0];

  useEffect(() => {
    if (!selectedId && groups.length) setSelectedId(groups[0].id);
    if (selectedId && !groups.some((group) => group.id === selectedId)) {
      setSelectedId(groups[0]?.id || null);
    }
  }, [groups, selectedId]);

  const memberIds = useMemo(
    () => new Set((selected?.tags || []).map((tag) => String(tag.id))),
    [selected],
  );
  const matchesSearch = (tag) =>
    tag.name.toLowerCase().includes(search.trim().toLowerCase());
  const included = tags.filter(
    (tag) => memberIds.has(String(tag.id)) && matchesSearch(tag),
  ).sort(byName);
  const available = tags.filter(
    (tag) => !memberIds.has(String(tag.id)) && matchesSearch(tag),
  ).sort(byName);

  const refresh = async (preferredId = selected?.id) => {
    const response = await TagGroupsService.list();
    const nextGroups = response.data || [];
    setSettings((current) => ({ ...current, tag_groups: nextGroups }));
    setSelectedId(
      nextGroups.some((group) => group.id === preferredId)
        ? preferredId
        : nextGroups[0]?.id || null,
    );
  };

  const run = async (operation, preferredId) => {
    setBusy(true);
    try {
      const result = await operation();
      await refresh(preferredId || result?.data?.id);
    } catch (error) {
      UIkit.notification({
        message: error.response?.data?.error || "Unable to update tag groups",
        status: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const createGroup = () => {
    const nextLabel = label.trim();
    if (!nextLabel) return;
    run(async () => {
      const response = await TagGroupsService.create(nextLabel);
      setLabel("");
      return response;
    }, null);
  };

  const renameGroup = () => {
    if (!selected) return;
    const nextLabel = window.prompt("Rename tag group", selected.label)?.trim();
    if (!nextLabel || nextLabel === selected.label) return;
    run(() => TagGroupsService.rename(selected.id, nextLabel), selected.id);
  };

  const deleteGroup = () => {
    if (!selected) return;
    if (!window.confirm(`Delete ${selected.label}? Tags will not be deleted.`)) return;
    run(() => TagGroupsService.remove(selected.id), null);
  };

  const moveTag = (tag, add) => {
    if (!selected) return;
    run(
      () =>
        add
          ? TagGroupsService.addTag(selected.id, tag.id)
          : TagGroupsService.removeTag(selected.id, tag.id),
      selected.id,
    );
  };

  return (
    <li
      className={`setting-tab-content ${active === "tag_groups" ? "uk-active" : ""}`}
    >
      <h2>Tag Groups</h2>
      <p className="uk-text-muted">
        Organize tags into folders without changing where or how tags are used.
      </p>
      <div className="tag-groups-layout">
        <section className="tag-groups-panel tag-groups-sidebar">
          <div className="tag-groups-panel-heading">
            <h3>Groups</h3>
            <span>{groups.length}</span>
          </div>
          <ul className="tag-groups-list">
            {groups.map((group) => (
              <li key={group.id}>
                <button
                  type="button"
                  className={group.id === selected?.id ? "is-active" : ""}
                  onClick={() => setSelectedId(group.id)}
                >
                  <span>{group.label}</span>
                  <span>{group.tags?.length || 0}</span>
                </button>
              </li>
            ))}
          </ul>
          {groups.length === 0 && (
            <div className="tag-groups-empty">Create a group to start organizing tags.</div>
          )}
          <div className="tag-groups-create">
            <input
              className="uk-input"
              value={label}
              placeholder="New group name"
              onChange={(event) => setLabel(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && createGroup()}
            />
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              disabled={busy || !label.trim()}
              onClick={createGroup}
            >
              Add
            </button>
          </div>
          <div className="tag-groups-actions">
            <button
              className="tag-groups-icon-button"
              type="button"
              aria-label="Rename group"
              uk-tooltip="title: Rename group; delay: 300"
              disabled={!selected || busy}
              onClick={renameGroup}
            >
              <FeatherIcon name="edit-2" />
            </button>
            <button
              className="tag-groups-icon-button tag-groups-delete-button"
              type="button"
              aria-label="Delete group"
              uk-tooltip="title: Delete group; delay: 300"
              disabled={!selected || busy}
              onClick={deleteGroup}
            >
              <FeatherIcon name="trash-2" />
            </button>
          </div>
        </section>

        <section className="tag-groups-panel tag-groups-membership">
          {selected ? (
            <>
              <div className="tag-groups-panel-heading">
                <div>
                  <h3>{selected.label}</h3>
                  <p>A tag can be included in more than one group.</p>
                </div>
                <input
                  className="uk-input tag-groups-search"
                  type="search"
                  placeholder="Search tags"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="tag-groups-columns">
                <TagColumn
                  title="Tags in this Group"
                  empty="No tags in this group yet."
                  tags={included}
                  actionIcon="trash-2"
                  actionLabel="Remove"
                  busy={busy}
                  onAction={(tag) => moveTag(tag, false)}
                  included
                />
                <TagColumn
                  title="Available Tags"
                  empty="Every matching tag is already in this group."
                  tags={available}
                  actionIcon="plus"
                  actionLabel="Add"
                  busy={busy}
                  onAction={(tag) => moveTag(tag, true)}
                />
              </div>
            </>
          ) : (
            <div className="tag-groups-empty tag-groups-empty-main">
              Select or create a group to manage its tags.
            </div>
          )}
        </section>
      </div>
    </li>
  );
};

const TagColumn = ({
  title,
  empty,
  tags,
  actionIcon,
  actionLabel,
  onAction,
  busy,
  included = false,
}) => (
  <div className="tag-groups-column">
    <div className="tag-groups-column-title">
      <h4>{title}</h4>
      <span>{tags.length}</span>
    </div>
    <ul>
      {tags.map((tag) => (
        <li key={tag.id}>
          <span>{included ? "✓ " : ""}{tag.name}</span>
          <button
            className={`tag-groups-icon-button ${actionLabel === "Remove" ? "tag-groups-delete-button" : ""}`}
            type="button"
            aria-label={`${actionLabel} ${tag.name}`}
            uk-tooltip={`title: ${actionLabel} ${tag.name}; delay: 300`}
            disabled={busy}
            onClick={() => onAction(tag)}
          >
            <FeatherIcon name={actionIcon} />
          </button>
        </li>
      ))}
    </ul>
    {tags.length === 0 && <p className="tag-groups-empty">{empty}</p>}
  </div>
);

export default TagGroups;
